import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { blogPosts } from '../mockData';
import { existingBlogPosts } from '../seo/blogMigration';
import { handleLegacyRedirects } from '../utils/redirects';
import SEO from '../components/SEO';

const BlogPostPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const allPosts = [...blogPosts, ...existingBlogPosts];
    const post = allPosts.find(p => p.slug === slug || p.newSlug === slug);

    useEffect(() => {
        const redirectUrl = handleLegacyRedirects();
        if (redirectUrl) {
            navigate(redirectUrl, { replace: true });
        }
    }, [navigate]);

    if (!post) {
        return (
            <>
                <SEO 
                    title="Articol negăsit - Oana Tenea"
                    description="Articolul pe care îl cauți nu există sau a fost actualizat."
                />
                <div className="bg-stone-50 dark:bg-stone-950 py-24 text-center">
                    <div className="container mx-auto px-6 max-w-xl">
                        <div className="w-16 h-16 rounded-full bg-stone-200 dark:bg-stone-800 text-stone-500 mx-auto flex items-center justify-center mb-6">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h1 className="font-display text-3xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                            Articolul nu a fost găsit
                        </h1>
                        <p className="text-stone-600 dark:text-stone-400 mb-8">
                            Articolul pe care îl cauți a fost mutat sau nu mai este disponibil.
                        </p>
                        <Link 
                            to="/blog"
                            className="inline-flex items-center px-7 py-3.5 bg-terracotta text-white font-semibold rounded-full shadow-warm hover:bg-terracotta-dark transition-colors"
                        >
                            Înapoi la toate articolele
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('ro-RO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <>
            <SEO 
                title={post.title}
                description={post.metaDescription || post.excerpt}
                slug={post.slug || post.newSlug}
                publishDate={post.publishDate}
                type="article"
                category={post.category}
                tags={post.tags}
                image={post.imageUrl || post.image}
            />
            
            <article className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 py-12 sm:py-20 transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
                    
                    {/* Breadcrumbs */}
                    <nav className="flex items-center space-x-2 text-xs text-stone-500 dark:text-stone-400 mb-8" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-terracotta transition-colors">Acasă</Link>
                        <span>/</span>
                        <Link to="/blog" className="hover:text-terracotta transition-colors">Blog</Link>
                        <span>/</span>
                        <span className="text-stone-800 dark:text-stone-200 truncate max-w-[200px] sm:max-w-xs">{post.title}</span>
                    </nav>

                    {/* Article Header */}
                    <header className="mb-10 text-center sm:text-left">
                        {/* Meta Tags Bar */}
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 mb-6">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light border border-terracotta-200/60 dark:border-stone-700">
                                {post.category}
                            </span>
                            {post.tags && post.tags.map((tag, i) => (
                                <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400">
                                    #{tag}
                                </span>
                            ))}
                            <span className="text-xs text-stone-400 dark:text-stone-500 font-medium ml-auto hidden sm:inline-flex items-center">
                                <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                {post.readingTime} min lectură
                            </span>
                        </div>

                        {/* Title */}
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 dark:text-stone-100 leading-tight mb-6 tracking-tight">
                            {post.title}
                        </h1>

                        {/* Author & Date Bar */}
                        <div className="flex items-center justify-between py-4 border-y border-stone-200/70 dark:border-stone-800 text-xs sm:text-sm text-stone-500 dark:text-stone-400">
                            <div className="flex items-center space-x-3">
                                <img 
                                    src="/OanaTenea.png" 
                                    alt="Oana Tenea" 
                                    className="w-10 h-10 rounded-full object-cover border border-stone-300 dark:border-stone-700"
                                />
                                <div>
                                    <div className="font-bold text-stone-900 dark:text-stone-100">Oana Tenea</div>
                                    <div className="text-[11px] text-terracotta">Psihoterapeut CBT & Schema Therapy</div>
                                </div>
                            </div>

                            <time dateTime={post.publishDate}>
                                {formatDate(post.publishDate)}
                            </time>
                        </div>
                    </header>

                    {/* Featured Image */}
                    {(post.imageUrl || post.image) && (
                        <div className="mb-12 rounded-3xl overflow-hidden shadow-elevated border border-stone-200/80 dark:border-stone-800 aspect-[16/9] sm:aspect-[21/9]">
                            <img 
                                src={post.imageUrl || post.image} 
                                alt={post.imageAlt || post.title} 
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Article Content */}
                    <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-12 shadow-elevated border border-stone-200/80 dark:border-stone-800 mb-12">
                        <div 
                            className="prose prose-base sm:prose-lg max-w-none dark:prose-invert prose-stone prose-headings:font-display prose-headings:text-stone-900 dark:prose-headings:text-stone-100 prose-headings:font-bold prose-p:text-stone-700 dark:prose-p:text-stone-300 prose-p:leading-relaxed prose-strong:text-stone-900 dark:prose-strong:text-white prose-li:text-stone-700 dark:prose-li:text-stone-300"
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />
                    </div>

                    {/* Consultation Call to Action */}
                    <div className="bg-gradient-to-br from-terracotta-50 to-amber-50/50 dark:from-stone-900 dark:to-stone-800 rounded-3xl p-8 sm:p-10 border border-terracotta-200/60 dark:border-stone-700 text-center shadow-soft">
                        <div className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center mx-auto mb-4">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-3">
                            Ai nevoie de suport personalizat?
                        </h3>
                        <p className="text-stone-600 dark:text-stone-400 text-sm sm:text-base max-w-lg mx-auto mb-8 leading-relaxed">
                            Dacă acest subiect rezonează cu provocările tale actuale, te aștept la o consultație la cabinetul din București (Calea Dorobanți) sau online.
                        </p>
                        <Link 
                            to="/contact"
                            className="inline-flex items-center px-8 py-3.5 bg-terracotta hover:bg-terracotta-dark text-white font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 text-sm sm:text-base"
                        >
                            Programează o consultație
                        </Link>
                    </div>

                    {/* Back to Blog */}
                    <div className="text-center mt-10">
                        <Link 
                            to="/blog"
                            className="inline-flex items-center text-sm font-semibold text-stone-600 dark:text-stone-400 hover:text-terracotta transition-colors"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Înapoi la toate articolele
                        </Link>
                    </div>

                </div>
            </article>
        </>
    );
};

export default BlogPostPage;
